import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate, todayISO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Pencil, Send, CheckCircle2, XCircle, ClipboardList, ArrowRightCircle, PackageCheck, ShoppingCart } from "lucide-react";
import { generateQuotationPDF } from "../lib/quotationPdf";
import PdfPreviewDialog from "../components/PdfPreviewDialog";

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100",
  sent: "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
  rejected: "bg-red-100 text-red-700 border-red-200 hover:bg-red-100",
};
const STATUS_LABEL = { draft: "DRAFT", sent: "SENT", approved: "APPROVED", rejected: "REJECTED" };

const blank = (company = {}) => ({
  customer_id: "",
  customer_name: "",
  customer_address: "",
  customer_email: "",
  customer_phone: "",
  customer_pic: "",
  quote_date: todayISO(),
  valid_until: "",
  items: [{ product_id: "", sku: "", name: "", description: "", unit: "PCS", quantity: 1, price: 0 }],
  discount_type: "none",
  discount_value: 0,
  ppn_percent: company.default_ppn_percent ?? 11,
  include_ppn: company.default_include_ppn ?? true,
  terms_conditions:
    "1. Price include PPN 12%.\n" +
    "2. Payment Term: TT 30 Days after invoice received.\n" +
    "3. Validity Term: 30 Days from quotation date.\n" +
    "4. Delivery: 1-2 weeks after PO received.",
  notes: "",
  status: "draft",
});

export default function Quotations() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [company, setCompany] = useState({});
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank());

  // Convert dialogs
  const [convertQuote, setConvertQuote] = useState(null);
  const [convertMode, setConvertMode] = useState("so"); // "so" | "po"
  const [stockCheck, setStockCheck] = useState(null);
  const [convertPO, setConvertPO] = useState({ supplier_id: "", include_ppn: false, ppn_percent: 11, only_shortage: true, notes: "" });
  const [convertSO, setConvertSO] = useState({ auto_approve: true, notes: "" });
  const [poPrices, setPoPrices] = useState({}); // { product_id: buy_price }
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const load = () => Promise.all([
    api.get("/quotations").then((r) => setItems(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}),
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}),
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}),
    api.get("/company-profile").then((r) => setCompany(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const pickProduct = (i, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updItem(i, {
      product_id: p.id,
      sku: p.sku,
      name: p.name,
      unit: p.unit || "PCS",
      price: Number(p.sale_price || p.sell_price || p.selling_price || p.price || 0) || 0,
    });
  };

  const openConvert = async (q, mode) => {
    setConvertQuote(q);
    setConvertMode(mode);
    setConvertPO({ supplier_id: "", include_ppn: convertQuote?.include_ppn ?? (company.default_include_ppn ?? false), ppn_percent: q.ppn_percent || company.default_ppn_percent || 11, only_shortage: true, notes: "" });
    setConvertSO({ auto_approve: true, notes: "" });
    // init harga PO dari purchase_price produk terkini (harga terbaru dari inventory)
    const priceMap = {};
    (q.items || []).forEach((it) => {
      if (!it.product_id) return;
      const p = products.find((x) => x.id === it.product_id);
      priceMap[it.product_id] = Number(p?.purchase_price || p?.buy_price || 0) || 0;
    });
    setPoPrices(priceMap);
    try {
      const r = await api.get(`/quotations/${q.id}/stock-check`);
      setStockCheck(r.data);
    } catch (e) { setStockCheck({ all_sufficient: false, rows: [] }); }
  };

  // Baris PO yang akan dikirim (respect only_shortage)
  const poRows = (stockCheck?.rows || []).filter((r) => r.linked && (convertPO.only_shortage ? r.shortage > 0 : true))
    .map((r) => ({ ...r, po_qty: convertPO.only_shortage ? r.shortage : r.quantity, price: Number(poPrices[r.product_id] || 0) }));
  const poGross = poRows.reduce((s, r) => s + r.po_qty * r.price, 0);
  const poPPN = convertPO.include_ppn ? poGross * (Number(convertPO.ppn_percent) || 0) / 100 : 0;
  const poGrandTotal = poGross + poPPN;

  const doConvertSO = async () => {
    try {
      const r = await api.post(`/quotations/${convertQuote.id}/convert-to-so`, convertSO);
      toast.success(`SO ${r.data.sales_order.so_number} berhasil dibuat`);
      setConvertQuote(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal convert"); }
  };
  const doConvertPO = async () => {
    if (!convertPO.supplier_id) return toast.error("Pilih supplier");
    if (poRows.length === 0) return toast.error("Tidak ada item yang akan di-PO");
    const sup = suppliers.find((s) => s.id === convertPO.supplier_id);
    try {
      const r = await api.post(`/quotations/${convertQuote.id}/convert-to-po`, {
        ...convertPO,
        supplier_name: sup?.name || "",
        override_prices: poPrices,
      });
      toast.success(`PO ${r.data.purchase_order.po_number} berhasil dibuat`);
      setConvertQuote(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal convert"); }
  };

  const filtered = tab === "all" ? items : items.filter((q) => q.status === tab);

  const openNew = () => { setForm(blank(company)); setEditingId(null); setOpen(true); };
  const openEdit = (q) => {
    setForm({
      ...blank(company),
      ...q,
      discount_type: q.discount_type || "none",
      items: q.items.length ? q.items : blank(company).items,
    });
    setEditingId(q.id);
    setOpen(true);
  };
  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) return;
    setForm((prev) => ({
      ...prev, customer_id: c.id, customer_name: c.name,
      customer_address: c.address || "", customer_phone: c.phone || "",
      customer_email: c.email || "", customer_pic: c.contact || "",
    }));
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", sku: "", name: "", description: "", unit: "PCS", quantity: 1, price: 0 }] });
  const updItem = (i, patch) => {
    const list = [...form.items];
    list[i] = { ...list[i], ...patch };
    setForm({ ...form, items: list });
  };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.price) || 0), 0);
  const disc = form.discount_type === "percent"
    ? Math.min(subtotal, subtotal * (Number(form.discount_value) || 0) / 100)
    : form.discount_type === "amount"
      ? Math.min(subtotal, Number(form.discount_value) || 0)
      : 0;
  const net = subtotal - disc;
  const ppn = form.include_ppn ? net * (Number(form.ppn_percent) || 0) / 100 : 0;
  const total = net + ppn;

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Nama klien wajib diisi");
    if (form.items.length === 0) return toast.error("Minimal 1 item");
    if (form.items.some((it) => !it.name.trim())) return toast.error("Setiap item wajib punya nama");
    try {
      const payload = {
        ...form,
        customer_id: form.customer_id || null,
        discount_type: form.discount_type === "none" ? null : form.discount_type,
        discount_value: form.discount_type === "none" ? 0 : Number(form.discount_value) || 0,
        ppn_percent: Number(form.ppn_percent) || 0,
        items: form.items.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 0,
          price: Number(it.price) || 0,
        })),
      };
      if (editingId) await api.put(`/quotations/${editingId}`, payload);
      else await api.post("/quotations", payload);
      toast.success(editingId ? "Quotation diperbarui" : "Quotation dibuat");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const changeStatus = async (q, status) => {
    try {
      await api.post(`/quotations/${q.id}/status`, { status });
      toast.success(`Status diubah menjadi ${STATUS_LABEL[status]}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Hapus quotation ${q.quote_number}?`)) return;
    try { await api.delete(`/quotations/${q.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const printPDF = (q) => {
    try {
      const res = generateQuotationPDF({ quote: q, company, preview: true });
      if (res?.url) setPdfPrev({ open: true, url: res.url, filename: res.filename, title: `Preview Quotation ${q.quote_number}` });
    } catch (e) { toast.error("Gagal cetak: " + e.message); }
  };

  const totals = {
    all: items.length,
    draft: items.filter((q) => q.status === "draft").length,
    sent: items.filter((q) => q.status === "sent").length,
    approved: items.filter((q) => q.status === "approved").length,
    rejected: items.filter((q) => q.status === "rejected").length,
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Penjualan</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Quotation</h1>
          <p className="text-sm text-slate-500 mt-1">Penawaran harga untuk klien — dapat di-cetak sebagai PDF profesional & alur status Draft → Sent → Approved/Rejected.</p>
        </div>
        <Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="quote-new-btn"><Plus size={16} className="mr-2" />Buat Penawaran</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["all", "draft", "sent", "approved", "rejected"].map((s) => (
          <Card key={s} className={`card-elevated ${tab === s ? "ring-2 ring-navy-900" : ""} cursor-pointer transition`} onClick={() => setTab(s)}>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{s === "all" ? "Total" : STATUS_LABEL[s]}</div>
              <div className="font-display text-xl font-extrabold text-navy-900">{totals[s]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="quotations-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. Quote</TableHead>
                <TableHead>Klien</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Berlaku s/d</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.quote_number}`}>
                  <TableCell className="font-mono text-xs font-semibold text-navy-900">{q.quote_number}</TableCell>
                  <TableCell>
                    <div className="font-medium">{q.customer_name}</div>
                    {q.customer_pic && <div className="text-xs text-slate-500">Attn: {q.customer_pic}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(q.quote_date)}</TableCell>
                  <TableCell className="text-sm">{q.valid_until ? formatDate(q.valid_until) : "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{q.items?.length || 0} baris</TableCell>
                  <TableCell className="text-right font-mono font-bold text-navy-900">{formatIDR(q.total)}</TableCell>
                  <TableCell><Badge className={STATUS_BADGE[q.status]}>{STATUS_LABEL[q.status]}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => printPDF(q)} data-testid={`quote-pdf-${q.quote_number}`}><Printer size={12} className="mr-1" />PDF</Button>
                    {q.status !== "approved" && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(q)} className="ml-1" data-testid={`quote-edit-${q.quote_number}`}><Pencil size={14} /></Button>
                    )}
                    {q.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(q, "sent")} className="ml-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`quote-send-${q.quote_number}`}><Send size={12} className="mr-1" />Kirim</Button>
                    )}
                    {q.status === "sent" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => changeStatus(q, "approved")} className="ml-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`quote-approve-${q.quote_number}`}><CheckCircle2 size={12} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => changeStatus(q, "rejected")} className="ml-1 border-red-300 text-red-700 hover:bg-red-50" data-testid={`quote-reject-${q.quote_number}`}><XCircle size={12} className="mr-1" />Reject</Button>
                      </>
                    )}
                    {q.status === "approved" && !q.converted_so_id && (
                      <Button size="sm" variant="outline" onClick={() => openConvert(q, "so")} className="ml-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid={`quote-conv-so-${q.quote_number}`}><ArrowRightCircle size={12} className="mr-1" />→ SO</Button>
                    )}
                    {q.status === "approved" && q.converted_so_id && (
                      <Badge className="ml-1 bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-[10px]">SO: {q.converted_so_number}</Badge>
                    )}
                    {(q.status === "approved" || q.status === "sent") && (
                      <Button size="sm" variant="outline" onClick={() => openConvert(q, "po")} className="ml-1 border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`quote-conv-po-${q.quote_number}`}><ShoppingCart size={12} className="mr-1" />→ PO</Button>
                    )}
                    {q.status !== "approved" && (
                      <Button size="sm" variant="ghost" onClick={() => remove(q)} className="ml-1"><Trash2 size={14} className="text-red-500" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Tidak ada quotation di filter ini</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Quotation" : "Buat Quotation Baru"}</DialogTitle></DialogHeader>

          <div className="space-y-4 text-sm">
            {/* Client info */}
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Data Klien</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label>Pilih Customer (opsional)</Label>
                  <Select value={form.customer_id} onValueChange={pickCustomer}>
                    <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="— Isi manual atau pilih —" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Nama Perusahaan *</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="quote-customer-name" />
                </div>
                <div className="md:col-span-2">
                  <Label>Alamat</Label>
                  <Textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
                </div>
                <div><Label>Email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
                <div><Label>Telepon</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
                <div><Label>Nama PIC (Attn)</Label><Input value={form.customer_pic} onChange={(e) => setForm({ ...form, customer_pic: e.target.value })} placeholder="Cth: Mr. Aditia" data-testid="quote-customer-pic" /></div>
                <div><Label>Tanggal Quote</Label><Input type="date" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} /></div>
                <div><Label>Valid Sampai</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid-until" /></div>
              </div>
            </CardContent></Card>

            {/* Items */}
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Item Penawaran</div>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="quote-add-item"><Plus size={12} className="mr-1" />Tambah Baris</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded-lg">
                    <div className="col-span-12 md:col-span-3">
                      <Label className="text-[10px] text-slate-500">Produk (Inventory)</Label>
                      <Select value={it.product_id || ""} onValueChange={(v) => pickProduct(i, v)}>
                        <SelectTrigger data-testid={`quote-item-product-${i}`} className="text-xs"><SelectValue placeholder="— Pilih dari stok / kosong utk jasa —" /></SelectTrigger>
                        <SelectContent className="max-h-72">{products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name} (stok: {p.stock})</SelectItem>
                        ))}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <Label className="text-[10px] text-slate-500">Nama Item {it.product_id && <span className="text-emerald-600">• linked</span>}</Label>
                      <Input value={it.name} onChange={(e) => updItem(i, { name: e.target.value })} placeholder="Cth: Kabel NYY 3x6" data-testid={`quote-item-name-${i}`} />
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <Label className="text-[10px] text-slate-500">Deskripsi</Label>
                      <Input value={it.description} onChange={(e) => updItem(i, { description: e.target.value })} placeholder="Detail teknis, merek" />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-[10px] text-slate-500">Unit</Label>
                      <Input value={it.unit} onChange={(e) => updItem(i, { unit: e.target.value })} placeholder="PCS" />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-[10px] text-slate-500">Qty</Label>
                      <Input type="number" value={it.quantity} onChange={(e) => updItem(i, { quantity: e.target.value })} data-testid={`quote-item-qty-${i}`} />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-[10px] text-slate-500">Harga Satuan</Label>
                      <Input type="number" value={it.price} onChange={(e) => updItem(i, { price: e.target.value })} data-testid={`quote-item-price-${i}`} />
                    </div>
                    <div className="col-span-2 md:col-span-12 flex items-center justify-between">
                      <div className="text-[11px] text-slate-500">Total: <span className="font-mono text-navy-900 font-bold">{formatIDR((Number(it.quantity) || 0) * (Number(it.price) || 0))}</span></div>
                      <Button size="sm" variant="ghost" onClick={() => rmItem(i)} disabled={form.items.length === 1}><Trash2 size={14} className="text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent></Card>

            {/* Summary */}
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Discount</Label>
                    <div className="flex gap-2">
                      <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v, discount_value: v === "none" ? 0 : form.discount_value })}>
                        <SelectTrigger className="w-40" data-testid="quote-disc-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Tanpa Diskon</SelectItem>
                          <SelectItem value="percent">Persen (%)</SelectItem>
                          <SelectItem value="amount">Nominal (Rp)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" value={form.discount_value} disabled={form.discount_type === "none"} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} className="flex-1" data-testid="quote-disc-value" />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 h-10">
                      <Switch checked={form.include_ppn} onCheckedChange={(v) => setForm({ ...form, include_ppn: v })} data-testid="quote-ppn-toggle" />
                      <Label className="text-xs">Kenakan PPN</Label>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Tarif PPN (%)</Label>
                      <Input type="number" value={form.ppn_percent} disabled={!form.include_ppn} onChange={(e) => setForm({ ...form, ppn_percent: Number(e.target.value) })} data-testid="quote-ppn-percent" />
                    </div>
                  </div>
                </div>

                <div className="text-sm space-y-1 bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between text-slate-600"><span>Total Amount</span><span className="font-mono">{formatIDR(subtotal)}</span></div>
                  {disc > 0 && <div className="flex justify-between text-red-600"><span>(-) Discount {form.discount_type === "percent" ? `(${form.discount_value}%)` : ""}</span><span className="font-mono">-{formatIDR(disc)}</span></div>}
                  {form.include_ppn && <div className="flex justify-between text-slate-600"><span>VAT {form.ppn_percent}%</span><span className="font-mono">{formatIDR(ppn)}</span></div>}
                  <div className="flex justify-between font-display font-bold text-navy-900 pt-2 border-t border-slate-300 text-base"><span>Grand Total</span><span className="font-mono" data-testid="quote-total">{formatIDR(total)}</span></div>
                </div>
              </div>
            </CardContent></Card>

            {/* Terms & Notes */}
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Terms & Conditions</Label>
                  <Textarea rows={5} value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} data-testid="quote-tc" />
                </div>
                <div>
                  <Label>Catatan Internal</Label>
                  <Textarea rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Tidak dicetak di PDF" />
                </div>
              </div>
            </CardContent></Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="quote-save">{editingId ? "Update" : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />

      {/* Convert Dialog */}
      <Dialog open={!!convertQuote} onOpenChange={(v) => !v && setConvertQuote(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {convertMode === "so" ? "Convert Quotation → Sales Order + Invoice" : "Convert Quotation → Purchase Order"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Quotation</div>
              <div className="font-mono text-navy-900 font-bold">{convertQuote?.quote_number}</div>
              <div className="text-xs text-slate-600">{convertQuote?.customer_name} • Grand Total: {formatIDR(convertQuote?.total || 0)}</div>
            </div>

            {/* Stock Check Table */}
            {stockCheck && (
              <Card className="border-slate-200"><CardContent className="p-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                  <PackageCheck size={14} className={stockCheck.all_sufficient ? "text-emerald-600" : "text-amber-600"} />
                  Stock Availability
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Stok</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{stockCheck.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{r.name} {!r.linked && <span className="text-red-500">(unlinked)</span>}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.quantity}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.stock ?? "-"}</TableCell>
                      <TableCell>
                        {!r.linked ? <Badge className="bg-red-100 text-red-700 border-red-200">Unlinked</Badge>
                          : r.sufficient ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Cukup</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 border-amber-200">Kurang {r.shortage}</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </CardContent></Card>
            )}

            {convertMode === "so" && (
              <Card className="border-slate-200"><CardContent className="p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <Switch checked={convertSO.auto_approve} onCheckedChange={(v) => setConvertSO({ ...convertSO, auto_approve: v })} data-testid="conv-so-auto-approve" />
                  <Label className="text-sm">Auto-Approve SO (langsung kurangi stok, buat Invoice & Journal)</Label>
                </div>
                <div>
                  <Label>Catatan SO</Label>
                  <Input value={convertSO.notes} onChange={(e) => setConvertSO({ ...convertSO, notes: e.target.value })} placeholder="Opsional" />
                </div>
                {!stockCheck?.all_sufficient && convertSO.auto_approve && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">⚠ Stok tidak mencukupi. Non-aktifkan auto-approve atau buat PO dulu.</div>
                )}
              </CardContent></Card>
            )}

            {convertMode === "po" && (
              <Card className="border-slate-200"><CardContent className="p-3 space-y-3">
                <div>
                  <Label>Supplier *</Label>
                  <Select value={convertPO.supplier_id} onValueChange={(v) => setConvertPO({ ...convertPO, supplier_id: v })}>
                    <SelectTrigger data-testid="conv-po-supplier"><SelectValue placeholder="— Pilih supplier —" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={convertPO.only_shortage} onCheckedChange={(v) => setConvertPO({ ...convertPO, only_shortage: v })} />
                  <Label className="text-sm">Hanya item yang stok-nya kurang</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={convertPO.include_ppn} onCheckedChange={(v) => setConvertPO({ ...convertPO, include_ppn: v })} />
                  <Label className="text-sm">Kenakan PPN pada PO</Label>
                  <Select value={String(convertPO.ppn_percent)} onValueChange={(v) => setConvertPO({ ...convertPO, ppn_percent: Number(v) })} disabled={!convertPO.include_ppn}>
                    <SelectTrigger className="w-24 h-9" data-testid="conv-po-ppn-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11">11%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="13">13%</SelectItem>
                      <SelectItem value="14">14%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Editable Harga Beli — mengikuti harga terbaru, bisa di-override */}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Harga Beli PO (dapat diedit)</div>
                  <div className="text-[11px] text-slate-500 mb-2">Default = harga beli terakhir di inventory. Ubah bila supplier memberi harga terbaru.</div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty PO</TableHead>
                      <TableHead className="text-right w-40">Harga Beli (Rp)</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {poRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">
                            <div className="font-medium">{r.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{r.sku}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">{r.po_qty}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              className="h-8 text-right font-mono text-xs"
                              value={poPrices[r.product_id] ?? 0}
                              onChange={(e) => setPoPrices({ ...poPrices, [r.product_id]: Number(e.target.value) || 0 })}
                              data-testid={`po-price-${r.sku || i}`}
                            />
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-bold text-navy-900">{formatIDR(r.po_qty * r.price)}</TableCell>
                        </TableRow>
                      ))}
                      {poRows.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="text-center py-4 text-xs text-slate-400">
                          {convertPO.only_shortage ? "Semua stok mencukupi — non-aktifkan 'Hanya item yang stok-nya kurang' bila tetap ingin buat PO." : "Tidak ada item ter-link ke inventory."}
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {poRows.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-2 mt-2 text-sm">
                      <div className="flex justify-between text-slate-600"><span>Subtotal PO</span><span className="font-mono">{formatIDR(poGross)}</span></div>
                      {convertPO.include_ppn && <div className="flex justify-between text-slate-600"><span>PPN 11%</span><span className="font-mono">{formatIDR(poPPN)}</span></div>}
                      <div className="flex justify-between font-display font-bold text-navy-900 pt-1 border-t border-slate-300"><span>Grand Total PO</span><span className="font-mono" data-testid="po-grand-total">{formatIDR(poGrandTotal)}</span></div>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Catatan PO</Label>
                  <Input value={convertPO.notes} onChange={(e) => setConvertPO({ ...convertPO, notes: e.target.value })} placeholder="Opsional" />
                </div>
              </CardContent></Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertQuote(null)}>Batal</Button>
            {convertMode === "so" ? (
              <Button className="bg-emerald-700 hover:bg-emerald-600 text-white" onClick={doConvertSO} data-testid="conv-so-submit"><ArrowRightCircle size={14} className="mr-1" />Buat Sales Order</Button>
            ) : (
              <Button className="bg-amber-600 hover:bg-amber-500 text-white" onClick={doConvertPO} data-testid="conv-po-submit"><ShoppingCart size={14} className="mr-1" />Buat Purchase Order</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
